import { ref, readonly } from 'vue';
import HttpService from '~/services/httpService';

const { get } = HttpService;

class FeatureFlagService {
  private _features = ref<Features>({} as Features);
  private _updateInterval = 60000; // 1 minuto por padrão
  private _refreshIntervalId: number | null = null;

  async CallInit(updateInterval = 60000): Promise<void | FeatureFlagService> {
    this._updateInterval = updateInterval;

    const success = await this.fetchFeatures();

    if (success) {
      this.onInit();
      return this;
    }

    console.info('⚠️ FEATURE_FLAG_SERVICE:: Initialization failed.');
  }

  private async fetchFeatures(): Promise<boolean> {
    try {
      const response = await get('/growthbook');

      const status = response.status || response.data.status;

      if (status === 401) {
        console.info('🚫 FEATURE_FLAG_SERVICE:: Unauthorized (401)');
        return false;
      }
      if (status === 403) {
        console.info('🚫 FEATURE_FLAG_SERVICE:: Forbidden (403)');
        return false;
      }
      if (status === 404) {
        console.info('❌ FEATURE_FLAG_SERVICE:: Not Found (404)');
        return false;
      }

      const features = response.data?.data[0];
      if (!features) {
        console.info('⚠️ FEATURE_FLAG_SERVICE:: No features received from server');
        return false;
      }

      this._features.value = features as Features;
      console.info('✅ FEATURE_FLAG_SERVICE:: Features loaded successfully');
      return true;
    } catch (error) {
      console.info('❌ FEATURE_FLAG_SERVICE:: Error fetching features:', error);
      return false;
    }
  }

  async refresh(): Promise<void | FeatureFlagService> {
    console.info('🔄 FEATURE_FLAG_SERVICE:: Refreshing feature flags...');
    const success = await this.fetchFeatures();

    if (success) {
      console.info('✅ FEATURE_FLAG_SERVICE:: Feature flags refreshed successfully');
    }

    return this;
  }

  isOn(flagKey: string): boolean {
    return !!this._features.value[flagKey as keyof Features]?.defaultValue;
  }

  isOff(flagKey: string): boolean {
    return !this._features.value[flagKey as keyof Features]?.defaultValue;
  }

  findByFlagName(flagName: string): Features[keyof Features] | null {
    return this._features.value[flagName as keyof Features] ?? null;
  }

  restart(): void {
    console.info('🔁 FEATURE_FLAG_SERVICE:: Restarting service...');
    this.clearInterval();
    this.CallInit(this._updateInterval);
  }

  private clearInterval() {
    if (this._refreshIntervalId) {
      clearInterval(this._refreshIntervalId);
      this._refreshIntervalId = null;
      console.info('🧹 FEATURE_FLAG_SERVICE:: Cleared existing interval');
    }
  }

  private onInit() {
    this.clearInterval();

    console.info('🌟 FEATURE_FLAG_SERVICE:: Initialized');
    console.info(`⏱️ Refresh every ${this._updateInterval / 1000}s`);

    this._refreshIntervalId = window.setInterval(async () => {
      await this.refresh();
      console.info(`🚀 Refreshed at ${new Date().toLocaleTimeString()}`);
    }, this._updateInterval);
  }

  get features() {
    return readonly(this._features);
  }
}

export default new FeatureFlagService();

export type Features = Record<string, { defaultValue: boolean }>;
